import React, { useState, useEffect, useRef } from 'react';
// import classNames from 'classnames';
import { EDTextEdit } from '../../BaseComponents/TextEdit/EDTextEdit';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import {
  getDateParts,
  getCurrentDateString,
  DateInputType,
} from '../../Utils/dateUtils';
import { IDate } from '../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/AnalysSettingsHandler/AnalysSettingsHandler';
import { CommonPropsAndTypes } from '../../BaseComponents/_Types/StandardComponentProps';
import {
  isDateKey,
  isSeperatorKey,
  isUtilityKey,
} from '../../Utils/parseUtils';
import { AttributeValueSizeType } from '../Attributes/AttributeValue/AttributeValue';

interface IProps {
  label: string;
  date: IDate;
  dateType?: DateInputType;
  placeholder?: string;
  className?: string;
  onBlur: (date: IDate, changeBothDates?: boolean) => void;
  fixedYear?: number;
  required?: boolean;
  partOfInterval: boolean;
  padding?: CommonPropsAndTypes.StandardSpacingType;
  autoFocus: boolean;
  dateTextRef?: React.MutableRefObject<HTMLInputElement | null>;
  tooltip?: string;
  size?: AttributeValueSizeType;
  paddingRight?: unknown;
  allMargins?: 'normal' | 'dense' | 'none';
  classes?: any;
}

export interface IDateError {
  text: string;
  type: DateInputType;
}

const styles = (theme: Theme) => ({
  input: {
    paddingLeft: 6,
  },
  formControlMargin: {
    marginTop: 0,
    marginBottom: 8,
  },
});

/**
 * Editeringskomponent för datum
 */
export const DateTextEdit: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const inputRef = useRef<HTMLInputElement>(undefined);
  const [dateText, setDateText] = useState(props.date.userString);
  useEffect(() => {
    setDateText(prevDateText => {
      if (prevDateText !== props.date.userString) {
        return props.date.userString;
      }

      return dateText;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.date]);

  const useOwnRef = props.dateTextRef === undefined;

  const getMaxInputLength = (): number => {
    switch (props.dateType) {
      case DateInputType.year:
        return 4;
      case DateInputType.yearMonth:
        return 7;
      case DateInputType.yearMonthDay:
        return 10;
      default:
        return 10;
    }
  };

  const onChange = (newValue: string) => {
    if (newValue.length <= getMaxInputLength()) {
      setDateText(newValue);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const useSeperator =
      props.dateType === undefined || props.dateType > DateInputType.year;

    /**On ENTER */
    if (e.keyCode === 13) {
      updateDateParts();
    } else if (e.keyCode === 38) {
      // Pil uppåt
      setDateText(getCurrentDateString(props.dateType));
      setTimeout(() => {
        if (useOwnRef && inputRef.current) {
          inputRef.current.setSelectionRange(
            inputRef.current.value.length,
            inputRef.current.value.length
          );
        } else if (
          !useOwnRef &&
          props.dateTextRef &&
          props.dateTextRef.current
        ) {
          props.dateTextRef.current.setSelectionRange(
            props.dateTextRef.current.value.length,
            props.dateTextRef.current.value.length
          );
        }
      }, 1);
    } else if (isSeperatorKey(e.keyCode, e.shiftKey) && !useSeperator) {
      e.preventDefault();
    } else if (
      (!isDateKey(e.keyCode) ||
        (isDateKey(e.keyCode) && e.shiftKey) ||
        (isDateKey(e.keyCode) && e.getModifierState('AltGraph'))) &&
      !isUtilityKey(e.keyCode) &&
      !isSeperatorKey(e.keyCode, e.shiftKey)
    ) {
      e.preventDefault();
    }
  };

  const updateDateParts = () => {
    const dateData = getDateParts(dateText, props.dateType);

    props.onBlur(
      {
        userString: dateText,
        ...dateData,
      },
      !props.partOfInterval
    );
  };

  const onBlur = () => {
    updateDateParts();
  };

  return (
    <EDTextEdit
      label={props.label}
      value={dateText}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      inputProps={{ placeholder: props.placeholder }}
      disableAnimation
      staticLabel
      error={Boolean(props.date.error)}
      helperText={props.date.error ? props.date.error.text : ''}
      usesHelperText
      inputRef={useOwnRef ? inputRef : props.dateTextRef}
      tooltip={props.tooltip}
      autoFocus={props.autoFocus}
      id={props.autoFocus ? 'autoFocus' : undefined}
      adornmentPadding="none"
      size={props.size}
      paddingRight={props.paddingRight}
      allMargins={props.allMargins}
    />
  );
};

export default withStyles(DateTextEdit, styles);
